import { __assign, __spreadArray } from 'tslib';
import { RecommendationEvent } from './recommendationEvent.esm.js';
import { TypeaheadEvent, EventName } from './typeaheadEvent.esm.js';
import { Optional } from './optional.esm.js';
import { Utils } from './utils.esm.js';
var LogType;
(function (LogType) {
  LogType["TYPEAHEAD_FOCUS"] = "typeahead-focus";
  LogType["TYPEAHEAD_SELECT"] = "typeahead-select";
  LogType["TYPEAHEAD_BLUR"] = "typeahead-blur";
  LogType["WIZARD_SUBMIT"] = "wizard-submit";
  LogType["RECOMMENDATION_SEEN"] = "recommendation-seen";
  LogType["RECOMMENDATION_SELECTED"] = "recommendation-selected";
})(LogType || (LogType = {}));
var TypeaheadEventLogger = /** @class */function () {
  // 1 - Create logger
  function TypeaheadEventLogger(analytics) {
    this.analytics = analytics;
    this.isEgtaLoggingEnabled = true;
    if (!TypeaheadEventLogger.sessionStore) {
      this.initSessionStore();
    }
  }
  // 2 - initialize logger.
  TypeaheadEventLogger.prototype.initLogger = function (metadata) {
    this.initSessionStore();
    this.initializeInputField();
    var client = metadata.client,
      subLob = metadata.subLob,
      lob = metadata.lob,
      locale = metadata.locale,
      siteId = metadata.siteId,
      personalize = metadata.personalize,
      enableEgta = metadata.enableEgta;
    TypeaheadEventLogger.sessionStore.metadata = {
      client: client,
      subLob: subLob,
      lob: lob,
      locale: locale,
      siteId: siteId,
      personalize: personalize,
      enableEgta: enableEgta
    };
  };
  TypeaheadEventLogger.prototype.logTypeaheadRequest = function (query) {
    if (TypeaheadEventLogger.sessionStore) {
      TypeaheadEventLogger.sessionStore.query = query;
    }
  };
  TypeaheadEventLogger.prototype.logTypeaheadResponse = function (response) {
    TypeaheadEventLogger.sessionStore.response = response;
    TypeaheadEventLogger.sessionStore.selected = undefined;
    var recEventMessage = RecommendationEvent.getRecommendationSeenMessage(response, TypeaheadEventLogger.sessionStore.fieldName || '');
    if (recEventMessage) {
      this.trackEventToUisPrime(this.getReferrerId(LogType.RECOMMENDATION_SEEN), LogType.RECOMMENDATION_SEEN, false, [recEventMessage]);
    }
  };
  TypeaheadEventLogger.prototype.logTypeaheadOpen = function (prefill) {
    TypeaheadEventLogger.sessionStore.prefill = prefill;
    this.initializeInputField();
    if (this.isEgtaLoggingEnabled) {
      this.trackEventToUisPrime(this.getReferrerId(LogType.TYPEAHEAD_FOCUS), LogType.TYPEAHEAD_FOCUS, false, [new TypeaheadEvent(EventName.FOCUS, TypeaheadEventLogger.sessionStore).getTypeaheadEventMessage()]);
    }
  };
  TypeaheadEventLogger.prototype.logSuggestionSelected = function (selected) {
    var isCurrLoc = (selected === null || selected === void 0 ? void 0 : selected.type) === 'CURRENT_LOCATION';
    if (Optional.ofNullable(selected).isPresent() && this.essResponseIsNotEmpty()) {
      TypeaheadEventLogger.sessionStore.selected = TypeaheadEventLogger.sessionStore.response.sr[selected.index - 1];
    }
    var eventName = isCurrLoc ? EventName.CURRENT_LOCATION : EventName.SELECT;
    var taEvent = new TypeaheadEvent(eventName, TypeaheadEventLogger.sessionStore);
    var recEventMessage = RecommendationEvent.getRecommendationClickedMessage(TypeaheadEventLogger.sessionStore.selected, TypeaheadEventLogger.sessionStore.response, TypeaheadEventLogger.sessionStore.fieldName || '');
    if (this.isEgtaLoggingEnabled) {
      var uisPrimeMessages = [taEvent.getTypeaheadEventMessage()];
      if (recEventMessage) {
        this.trackEventToUisPrime(this.getReferrerId(LogType.RECOMMENDATION_SELECTED), LogType.RECOMMENDATION_SELECTED, false, [recEventMessage]);
      }
      this.trackEventToUisPrime(this.getReferrerId(), LogType.TYPEAHEAD_SELECT, false, uisPrimeMessages);
    }
    return taEvent.getFlattenedJsonMessage();
  };
  /* The following two methods to be used by blossom-ui / buttercup / any other client who uses typeahead*/
  TypeaheadEventLogger.prototype.logTypeaheadBlurEvent = function () {
    var _a;
    var messages = this.buildUisPrimeMessages();
    if (this.isEgtaLoggingEnabled) {
      messages.push(new TypeaheadEvent(EventName.BLUR, TypeaheadEventLogger.sessionStore).getTypeaheadEventMessage());
    }
    this.trackEventToUisPrime(this.getReferrerId(), LogType.TYPEAHEAD_BLUR, false, messages);
    TypeaheadEventLogger.sessionStore.noOfBackspaces = 0;
    (_a = TypeaheadEventLogger.sessionStore.input) === null || _a === void 0 ? void 0 : _a.removeEventListener('keydown', this.keyDown);
  };
  TypeaheadEventLogger.prototype.isResolveNoSuggestionSelected = function (flag) {
    TypeaheadEventLogger.sessionStore.isResolve = flag;
  };
  TypeaheadEventLogger.prototype.logWizardSubmitEvent = function () {
    // Disabling since it is not consistent and also to fix - https://jira.expedia.biz/browse/ECOMSVC-4216
    // this.trackEventToUisPrime(this.getReferrerId(), LogType.WIZARD_SUBMIT, true, this.buildUisPrimeMessages());
    this.resetLoggerState();
  };
  TypeaheadEventLogger.prototype.trackEventToUisPrime = function (rfrr, linkName, trackImmediate, messages) {
    var _a, _b;
    try {
      (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.trackEvent(rfrr, linkName, (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.omnitureData, null, trackImmediate, messages);
    } catch (e) {
      Utils.logError(__assign(__assign({}, e), {
        message: 'Failed typeahead event tracking to UIS Prime'
      }));
    }
  };
  TypeaheadEventLogger.prototype.buildUisPrimeMessages = function () {
    if (Utils.isOrigin(TypeaheadEventLogger.sessionStore.fieldName || '')) {
      TypeaheadEventLogger.leg.origin = __assign({}, TypeaheadEventLogger.sessionStore);
    } else {
      TypeaheadEventLogger.leg.dest = __assign({}, TypeaheadEventLogger.sessionStore);
    }
    return __spreadArray(__spreadArray([], this.getTrackingDataEvents(), true), [this.getSelectedSuggestionEvents()], false);
  };
  TypeaheadEventLogger.prototype.getTrackingDataEvents = function () {
    var searchTerm = this.getUserTypedQuery(TypeaheadEventLogger.sessionStore);
    var numOfCharsTyped = searchTerm ? searchTerm.length : 0;
    var trackingData = [];
    /* To get the position of selected suggestion [event44] */
    if (this.isASuggestionSelected()) {
      trackingData.push(
      // @ts-ignore
      Object.freeze({
        messageContent: {
          name: 'TypeaheadSelectionDepth',
          value: this.getSelectedSuggestionPosition()
        },
        schemaName: 'events'
      }));
    }
    /* To see if user interacted with typeahead or not [event45] */
    if (TypeaheadEventLogger.sessionStore.response) {
      trackingData.push(
      // @ts-ignore
      Object.freeze({
        messageContent: {
          name: 'TypeaheadSearch',
          value: 0
        },
        schemaName: 'events'
      }));
    }
    /* To get the number of characters typed by the user [event46] */
    trackingData.push(
    // @ts-ignore
    Object.freeze({
      messageContent: {
        name: 'TypeaheadCharactersTyped',
        value: numOfCharsTyped
      },
      schemaName: 'events'
    }));
    return trackingData;
  };
  TypeaheadEventLogger.prototype.getSelectedSuggestionEvents = function () {
    var originSuggestionData = this.getSelectedSuggestionData('origin', TypeaheadEventLogger.leg.origin);
    var destSuggestionData = this.getSelectedSuggestionData('dest', TypeaheadEventLogger.leg.dest);
    return Object.freeze({
      messageContent: {
        selectedSuggestions: [originSuggestionData, destSuggestionData]
      },
      schemaName: 'allSelectedSuggestions'
    });
  };
  TypeaheadEventLogger.prototype.getSelectedSuggestionData = function (searchField, session) {
    var suggestion = session === null || session === void 0 ? void 0 : session.selected;
    var alternateName = searchField === 'origin' && !session ? 'OriginNotApplicable' : (session === null || session === void 0 ? void 0 : session.query) || (session === null || session === void 0 ? void 0 : session.prefill) || 'TANoneSelected';
    var userTypedQuery = this.getUserTypedQuery(TypeaheadEventLogger.sessionStore);
    var lastSearchName = suggestion && suggestion.regionNames && suggestion.regionNames.lastSearchName || alternateName;
    if (suggestion) {
      if (suggestion.essId) {
        return {
          essRequestId: (TypeaheadEventLogger.sessionStore.response || {
            rid: '-'
          }).rid,
          lastSearchName: lastSearchName,
          relation: suggestion && suggestion.hierarchyInfo && suggestion.hierarchyInfo.relation && suggestion.hierarchyInfo.relation[0] || '-',
          searchField: searchField,
          sourceName: suggestion.essId.sourceName,
          stringSourceId: (suggestion && suggestion.essId && suggestion.essId.sourceId || -1).toString(),
          suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
          userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
          userTypedQuery: userTypedQuery
        };
      }
      return {
        essRequestId: (TypeaheadEventLogger.sessionStore.response || {
          rid: '-'
        }).rid,
        lastSearchName: lastSearchName,
        relation: suggestion && suggestion.hierarchyInfo && suggestion.hierarchyInfo.relation && suggestion.hierarchyInfo.relation[0] || '-',
        searchField: searchField,
        sourceName: suggestion.sourceName ? suggestion.sourceName : 'UNKNOWN',
        stringSourceId: (suggestion && suggestion.essId && suggestion.essId.sourceId || -1).toString(),
        suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
        userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
        userTypedQuery: userTypedQuery
      };
    }
    return {
      essRequestId: (TypeaheadEventLogger.sessionStore.response || {
        rid: '-'
      }).rid,
      lastSearchName: lastSearchName,
      relation: suggestion && suggestion.hierarchyInfo && suggestion.hierarchyInfo.relation && suggestion.hierarchyInfo.relation[0] || '-',
      searchField: searchField,
      sourceName: 'UNKNOWN',
      stringSourceId: (suggestion && suggestion.essId && suggestion.essId.sourceId || -1).toString(),
      suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
      userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
      userTypedQuery: userTypedQuery
    };
  };
  TypeaheadEventLogger.prototype.getReferrerId = function (logType) {
    if (logType === LogType.RECOMMENDATION_SEEN) {
      return 'TYPEAHEAD.EGREC.RecSeen';
    }
    if (logType === LogType.RECOMMENDATION_SELECTED) {
      return 'TYPEAHEAD.EGREC.RecSelected';
    }
    var metadata = TypeaheadEventLogger.sessionStore.metadata || {};
    var parts = [];
    // @ts-ignore //
    parts.push(this.getLaunchView(metadata.client || ''));
    // @ts-ignore //
    parts.push(this.getWizard(metadata.client || ''));
    // @ts-ignore //
    parts.push(metadata.lob || 'Unknown');
    // @ts-ignore //
    parts.push(this.getShortLob(metadata.lob || '', metadata.subLob));
    // @ts-ignore //
    parts.push(metadata.locale || '-');
    // @ts-ignore //
    parts.push(metadata.siteId || '-');
    if (logType === LogType.TYPEAHEAD_FOCUS) {
      // @ts-ignore
      parts.push('TAStart');
    } else {
      // @ts-ignore
      parts.push(this.getTypeaheadTrigger());
      // @ts-ignore
      parts.push.apply(parts, this.getInstrumentationValue((metadata.lob || '').toLowerCase()));
    }
    return parts.join('|');
  };
  TypeaheadEventLogger.prototype.getLaunchView = function (clientId) {
    // eslint-disable-next-line no-param-reassign
    clientId = clientId.toLowerCase();
    if (clientId.indexOf('home') > -1) {
      return 'Home';
    }
    if (clientId.indexOf('flight') > -1) {
      return 'FLT';
    }
    if (clientId.indexOf('hotels.search') > -1) {
      // For hotels search page bernie appends "HOT.SR" so removing here
      return '';
    }
    if (clientId.indexOf('hotel') > -1) {
      return 'HTL';
    }
    if (clientId.indexOf('vacationrental') > -1) {
      return 'VR';
    }
    if (clientId.indexOf('pack') > -1) {
      return 'PKG';
    }
    if (clientId.indexOf('car') > -1) {
      return 'CAR';
    }
    if (clientId.indexOf('cru') > -1) {
      return 'CRU';
    }
    if (clientId.indexOf('lx') > -1) {
      return 'LX';
    }
    if (clientId.indexOf('rail') > -1) {
      return 'RAIL';
    }
    if (clientId.indexOf('open') > -1) {
      return 'OPEN';
    }
    return clientId;
  };
  TypeaheadEventLogger.prototype.getWizard = function (client) {
    switch (client.toLowerCase()) {
      case 'hotels.search':
      case 'flights.search':
      case 'open.search':
      case 'cars.search':
      case 'lx.Search':
      case 'packages.search':
        return 'UpdateSearch';
      default:
        return 'CoreWzd';
    }
  };
  TypeaheadEventLogger.prototype.getShortLob = function (lob, subLob) {
    switch (lob.toLowerCase()) {
      case 'flights':
        return "F.".concat(Utils.getSubLob(lob, subLob));
      case 'hotels':
        return 'H';
      case 'packages':
        return Utils.getSubLob(lob, subLob);
      case 'activities':
        return 'LX';
      case 'cruises':
        return 'CR';
      case 'cars':
        return 'C';
      default:
        return '-';
    }
  };
  TypeaheadEventLogger.prototype.getTypeaheadTrigger = function () {
    if (!this.essResponseIsNotEmpty()) {
      return 'TANoShow.TAPrevSearch';
    }
    var historyResultsShown = TypeaheadEventLogger.sessionStore.response.q === '';
    if (historyResultsShown) {
      return this.isASuggestionSelected() ? 'TAShow.TAFocus' : 'TAShow.TANoSelection';
    }
    return this.isASuggestionSelected() ? 'TAShow.TASelection' : 'TAShow.TANoSelection';
  };
  TypeaheadEventLogger.prototype.getInstrumentationValue = function (lob) {
    var result = [];
    var index = '-';
    if (this.getSelectedSuggestionPosition() >= 0) {
      index = this.getSelectedSuggestionPosition().toString();
    }
    var length = this.essResponseIsNotEmpty() ? TypeaheadEventLogger.sessionStore.response.sr.length : 0;
    if (lob === 'flights' || lob === 'hotels') {
      result.push("".concat(this.getHierarchyType(TypeaheadEventLogger.sessionStore.selected), "#").concat(index));
    } else {
      result.push("L#".concat(index));
    }
    result.push("".concat(this.getUserTypedQuery(TypeaheadEventLogger.sessionStore) && TypeaheadEventLogger.sessionStore.metadata && TypeaheadEventLogger.sessionStore.metadata.enableEgta ? 'EGTA' : 'ESS', "#").concat(length));
    result.push("UH#".concat(this.getNumberOfUserHistorySuggestions()));
    return result;
  };
  TypeaheadEventLogger.prototype.getHierarchyType = function (suggestion) {
    if (Optional.ofNullable(suggestion).isPresent()) {
      if (this.isChild(suggestion)) {
        return 'CL';
      }
      if (this.isParent(suggestion)) {
        return 'PL';
      }
    }
    return 'DL';
  };
  TypeaheadEventLogger.prototype.isChild = function (suggestion) {
    if (Optional.ofNullable(suggestion).isNotPresent()) {
      return false;
    }
    var relation = suggestion && suggestion.hierarchyInfo && suggestion.hierarchyInfo.relation || [];
    if (relation.length === 0) {
      return false;
    }
    return relation.indexOf('child') >= 0;
  };
  TypeaheadEventLogger.prototype.isParent = function (suggestion) {
    if (Optional.ofNullable(suggestion).isNotPresent() || !this.essResponseIsNotEmpty()) {
      return false;
    }
    var index = parseInt(Optional.ofNullable(suggestion.index).orElse('-1'), 10);
    if (index < 0 || TypeaheadEventLogger.sessionStore.response.sr.length <= index + 1) {
      return false;
    }
    return this.isChild(TypeaheadEventLogger.sessionStore.response.sr[index + 1]);
  };
  TypeaheadEventLogger.prototype.getNumberOfUserHistorySuggestions = function () {
    if (TypeaheadEventLogger.sessionStore.metadata && !!TypeaheadEventLogger.sessionStore.metadata.personalize) {
      if (!this.essResponseIsNotEmpty()) {
        return '0';
      }
      var suggestions = TypeaheadEventLogger.sessionStore.response.sr;
      return suggestions.filter(function (suggestion) {
        return suggestion.category === 'USERHISTORY';
      }).length.toString();
    }
    return '-';
  };
  /** ********* Util methods ************/
  TypeaheadEventLogger.prototype.essResponseIsNotEmpty = function () {
    return TypeaheadEventLogger.sessionStore.response && TypeaheadEventLogger.sessionStore.response.sr && TypeaheadEventLogger.sessionStore.response.sr.length > 0;
  };
  TypeaheadEventLogger.prototype.isASuggestionSelected = function () {
    return !!TypeaheadEventLogger.sessionStore.selected;
  };
  TypeaheadEventLogger.prototype.getUserTypedQuery = function (session) {
    return session.query === session.prefill ? '' : session.query;
  };
  TypeaheadEventLogger.prototype.getSelectedSuggestionPosition = function () {
    if (!this.isASuggestionSelected() || Optional.ofNullable(TypeaheadEventLogger.sessionStore.selected.index).isNotPresent()) {
      return -1;
    }
    try {
      return parseInt(TypeaheadEventLogger.sessionStore.selected.index, 10) + 1;
    } catch (e) {
      return -1;
    }
  };
  TypeaheadEventLogger.prototype.resetLoggerState = function () {
    this.initSessionStore();
    TypeaheadEventLogger.leg = {};
  };
  /* istanbul ignore next */
  TypeaheadEventLogger.prototype.keyDown = function (event) {
    if (event.key && ['Delete', 'Backspace'].indexOf(event.key) > -1 || event.keyCode && [8, 46].indexOf(event.keyCode) > -1) {
      TypeaheadEventLogger.sessionStore.noOfBackspaces += 1;
    }
  };
  TypeaheadEventLogger.prototype.initializeInputField = function () {
    var _a, _b;
    TypeaheadEventLogger.sessionStore.input = Utils.getDomElement();
    TypeaheadEventLogger.sessionStore.fieldName = Utils.getInputElementId(TypeaheadEventLogger.sessionStore.input);
    (_a = TypeaheadEventLogger.sessionStore.input) === null || _a === void 0 ? void 0 : _a.removeEventListener('keydown', this.keyDown);
    (_b = TypeaheadEventLogger.sessionStore.input) === null || _b === void 0 ? void 0 : _b.addEventListener('keydown', this.keyDown);
  };
  TypeaheadEventLogger.prototype.initSessionStore = function () {
    TypeaheadEventLogger.sessionStore = {
      metadata: {
        lob: Utils.UNKNOWN
      },
      noOfBackspaces: 0,
      query: '',
      href: window.location.href
    };
  };
  TypeaheadEventLogger.leg = {};
  return TypeaheadEventLogger;
}();
export { LogType, TypeaheadEventLogger };